import React, { useState, useEffect, useRef } from 'react';
import { isUnresolvedCreate, submitIdempotentCreate } from 'config/idempotentCreate';
import { CLIENT_DELIVERIES_URL } from '../createClient';
import { nameExists } from '../Utility/candidates';
import ClientInfo from './ClientInfo';
import { CLIENT_INFO_REQUIRED } from './clientInfoRequired';
import { useRequiredFields } from 'reusable/useRequiredFields';
import TaskAssignment from './TaskAssignment';
import DriverAssignment from './DriverAssignment';
import { DriverCandidate } from 'components/Driver/driverCandidates';
import { Task } from '../../../reusable/Client/dialog/AddTaskDialog';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSaveError } from 'reusable/useSaveError';

const AddClient: React.FC = () => {
  const { t } = useTranslation('client');
  const { showSaveError, showMessages, saveErrorPopup } = useSaveError();
  const [clientName, setClientName] = useState<string>('');
  const [clientDescription, setClientDescription] = useState<string>('');
  const [selectedDrivers, setSelectedDrivers] = useState<number[]>([]);
  // EV-493。選んだ運転者の名前など（候補の一覧は DriverAssignment がサーバーで検索する）。
  const [knownDrivers, setKnownDrivers] = useState<Record<number, DriverCandidate>>({});
  const [selectedTasks, setSelectedTasks] = useState<Task[]>([]);
  const navigate = useNavigate();
  const [showErrorTooltip, setShowErrorTooltip] = useState(false);
  // EV-436。名前・説明は required ではなく aria-required で伝え、空のまま送ったときだけ止める。
  const required = useRequiredFields(CLIENT_INFO_REQUIRED);
  // EV-128。送信中は「作成」を押せない。state の反映より前の二重の送信（Enter の連打など）も
  // 止めるため、ref でも持つ。
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  // EV-461（#387 の RV-703 と同じ型）。作成の結果が分からないまま（前回の X-Request-ID を残した）なら、入力を送った
  // 値のまま固定し、「同じ内容で送り直す」か、やめる（キャンセル）だけにする。以前は送った値に戻した後に入力を変えて
  // 送り直せ、新しい X-Request-ID になって、確定していた前回の作成を取り戻せなかった。
  const [unresolved, setUnresolved] = useState(false);
  const unresolvedRef = useRef(false);
  const setLocked = (value: boolean) => {
    unresolvedRef.current = value;
    setUnresolved(value);
  };
  const locked = submitting || unresolved;
  // EV-128。結果が分からなかった作成の X-Request-ID と本文は、入力を変えずにもう一度押したら
  // 同じ値で送り、前回が確定していればサーバーのリプレイを受け取る（二重に作らない）。
  // RV-130。画面を離れても失わないよう、このコンポーネントではなくタブの sessionStorage に
  // 残す（config/idempotentCreate.ts の submitIdempotentCreate）。
  // RV-130。画面を離れた後に届いた結果では、移動や表示をしない（残す値の更新だけ行う）。
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    // ADM-15。追加と解除で同じ関数を渡す。以前は別の無名関数を渡しており、解除されずに残っていた。
    const closeTooltip = () => setShowErrorTooltip(false);
    document.addEventListener('click', closeTooltip);
    return () => {
      document.removeEventListener('click', closeTooltip);
    };
  }, [showErrorTooltip]);

  // RV-129。送信中（送り直し・処理中の問い合わせ直しを含む）は送る内容を変えさせない。
  // 画面の操作は disabled にし（fieldset と各部品の disabled）、それを通らない経路
  // （開いたままのダイアログなど）も、ここで変更を捨てる。送った本文と画面が食い違うと、
  // 結果が分からなかった作成を「そのまま」押し直しても新しい X-Request-ID になり、二重に作る。
  const whenEditable =
    <A extends unknown[]>(update: (...args: A) => void) =>
    (...args: A) => {
      if (!submittingRef.current && !unresolvedRef.current) update(...args);
    };
  const editClientName = whenEditable(setClientName);
  const editClientDescription = whenEditable(setClientDescription);
  const editSelectedTasks = whenEditable(setSelectedTasks);
  const editSelectedDrivers = whenEditable(setSelectedDrivers);

  // Handle form submission to create a new client
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (submittingRef.current) return;
    // 以前のブラウザの検証（required）と同じく、作業の確認より前に止める。
    if (!required.check({ clientName, clientDescription })) return;
    if (selectedTasks.length === 0) {
      setShowErrorTooltip(true);
      return;
    }
    const newClient = {
      name: clientName,
      description: clientDescription,
      // EV-139。運転者は選んだ順番に意味が無い（サーバーは集合として割り当てる）。ID の順に
      // そろえ、同じ運転者なら同じ本文（同じ X-Request-ID・サーバーの本文の照合も一致）にする。
      // 作業は並び順に意味があるので、そのまま。
      assign_drivers: [...selectedDrivers].sort((a, b) => a - b),
      tasks: selectedTasks.map((task) => task.id),
    };
    const tasksSnapshot = selectedTasks;
    // RV-129。結果が分からず値を残すときは、画面を送った内容（保存した本文）に戻す。変更は
    // 送信中に止めているが、念のため、画面に見えている内容と送り直す内容を必ず一致させる。
    const restoreSubmitted = () => {
      setClientName(newClient.name);
      setClientDescription(newClient.description);
      setSelectedTasks(tasksSnapshot);
      setSelectedDrivers(newClient.assign_drivers);
    };
    submittingRef.current = true;
    setSubmitting(true);
    try {
      // EV-128（ADM-14 の AC1）。作成の操作ごとに X-Request-ID を作り、結果が分からない失敗では
      // 同じ値・同じ本文で送り直す。前回の結果が分からず、入力も変わっていなければ前回と同じ
      // 値を使う。期限（RV-127）・前回の値を残す条件（RV-128）・タブに残す（RV-130）・自分の値
      // だけを消す（EV-138）・送るときの利用者の印（EV-140）も含め、作業マスタ・機器・車両の
      // 作成と共通（config/idempotentCreate.ts の submitIdempotentCreate）。
      const outcome = await submitIdempotentCreate('client', CLIENT_DELIVERIES_URL, newClient);
      if (mounted.current) setLocked(isUnresolvedCreate(outcome));
      if (outcome.status === 'created') {
        if (mounted.current) navigate('/client');
      } else if (outcome.status === 'expired') {
        // RV-127。期限が近く、前回の値は使えない。古い値で黙って送らず、一覧を読み直して
        // 警告する。警告の後にもう一度押したときだけ、新しい値で作る。
        const messages = await messagesWithListCheck(t('ui.create-retry-expired'), newClient.name);
        if (mounted.current) showMessages(messages);
      } else if (outcome.status === 'unknown') {
        // 作成できたかを確かめられなかった（値は残した）。入力も残し、配送先の一覧を読み直す。
        console.error('Failed to add client', outcome.error);
        if (mounted.current) {
          restoreSubmitted();
          const messages = await messagesWithListCheck(
            t('ui.create-outcome-unknown'),
            newClient.name
          );
          if (mounted.current) showMessages(messages);
        }
      } else {
        console.error('Failed to add client', outcome.error);
        // RV-128。前回の値を残したとき（X-Request-ID の処理より前に断られた）は、画面を
        // 送った内容に戻す。
        if (outcome.keptRecord && mounted.current) restoreSubmitted();
        if (mounted.current) showSaveError(outcome.error);
      }
    } finally {
      submittingRef.current = false;
      if (mounted.current) setSubmitting(false);
    }
  };

  // EV-128。同じ名前の配送先があれば、作成済みでないかを確かめるよう添える。
  // 名前の一致は「作成できた」証拠にしない（前からある同じ名前の配送先かもしれないため）。
  // RV-127。期限が近く同じ値を使えないときの警告（lead）にも使う。
  // EV-493。以前は配送先を全件（最大5,000件・50回）読んで探していた。名前の完全一致でサーバーに1回だけ聞く。
  const messagesWithListCheck = async (lead: string, name: string): Promise<string[]> => {
    const messages = [lead];
    try {
      if (await nameExists(CLIENT_DELIVERIES_URL, name)) {
        messages.push(t('ui.create-same-name-exists'));
      }
    } catch (error) {
      console.error('Failed to reload clients', error);
    }
    return messages;
  };

  return (
    <section className="bg-white rounded-md m-4">
      <form onSubmit={handleSubmit}>
        {/* RV-129。送信中は入力・作業・運転者の操作をまとめて無効にする。 */}
        <fieldset disabled={locked} className="min-w-0" aria-describedby={unresolved ? 'add-client-unresolved' : undefined}>
          <ClientInfo
            clientName={clientName}
            setClientName={editClientName}
            clientDescription={clientDescription}
            setClientDescription={editClientDescription}
            requiredFields={required}
          />

          <hr />

          <TaskAssignment
            selectedTasks={selectedTasks}
            setSelectedTasks={editSelectedTasks}
            showErrorTooltip={showErrorTooltip}
            setShowErrorTooltip={() => setShowErrorTooltip(false)}
            disabled={locked}
          />

          <hr />

          <DriverAssignment
            selectedDrivers={selectedDrivers}
            knownDrivers={knownDrivers}
            handleDriverSelect={(driver) => {
              setKnownDrivers((known) => ({ ...known, [driver.id]: driver }));
              editSelectedDrivers((prev) => [...prev, driver.id]);
            }}
            handleDriverRemove={(driverId) =>
              editSelectedDrivers((prev) => prev.filter((id) => id !== driverId))
            }
            disabled={locked}
          />
        </fieldset>
        {unresolved ? (
          <p id="add-client-unresolved" className="mx-4 mt-4 text-body-2 text-neutral/300">
            {t('operator.unresolved-note', { ns: 'layout' })}
          </p>
        ) : null}
        <div className="flex justify-end mt-4 gap-4 border-t p-4">
          <button
            type="button"
            // RV-130。送信中は画面を離れさせない（離れると結果を受け取れない。値は残る）。
            disabled={submitting}
            className="bg-gray-200 font-semi-bold text-subtitle-2 text-primary/300 h-12 py-2 px-20 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => navigate('/client')}
          >
            {t('action.cancel')}
          </button>
          <button
            type="submit"
            disabled={submitting}
            aria-busy={submitting}
            className="bg-primary/300 font-medium text-subtitle-2 text-white h-12 py-2 px-12 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {unresolved ? t('operator.resend-same', { ns: 'layout' }) : t('action.create_client')}
          </button>
        </div>
      </form>
      {saveErrorPopup}
    </section>
  );
};

export default AddClient;
